import React, { Fragment, useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { isAndroid, isFirefox } from "react-device-detect";

import { LANGUAGE_EN } from "../constants";
import { LanguageOptionType, WithTranslationState } from "./withTranslation";
import { ArtworkObject, ArtWorkRecordsResult } from "../types/payloadTypes";
import { EmailForm } from "./EmailForm";
import { LanguageDropdown } from "./LanguageDropdown";
import { Share } from "./Share";
import { ContentBlock } from "./ContentBlock";
import { TemplatedResult } from "./TemplatedResult";
import { ScanButton } from "./ScanButton";
import { formatHtmlCaption, sanitizeRichText } from "../helpers/artWorkHelper";
import { track } from "../helpers/analytics";

type ArtworkDefaultProps = {
  artwork: ArtworkObject;
  result: ArtWorkRecordsResult;
  onSelectLanguage: (selectedLanguage: LanguageOptionType) => void;
  selectedLanguage: LanguageOptionType;
  emailCaptured: boolean;
  showEmailForm: boolean;
  onSubmitEmail: (email: string, callback?: (args?: any) => void) => void;
  langOptions: WithTranslationState["langOptions"];
  getTranslation: WithTranslationState["getTranslation"];
};

export const ArtworkDefault: React.FC<ArtworkDefaultProps> = ({
  artwork,
  result,
  onSelectLanguage,
  selectedLanguage,
  emailCaptured,
  showEmailForm,
  onSubmitEmail,
  langOptions,
  getTranslation,
}) => {
  // Component state
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [shortDescFontStyle, setShortDescStyle] = useState({});
  const startYCard2 = useRef<number>();
  const startYTrigger = useRef<number>();
  const startYPanel = useRef<number>();

  /** Updates state that email was captured and submits it to the server session */
  const handleSubmitEmail = (email: string) => {
    onSubmitEmail(email, () => setFormOpen(false));
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!formOpen) {
      track("email_form_open");
      setFormOpen(true);
      return;
    }
    // Open: a tap on the panel's own background (not the email form inside it)
    // closes it, so you can back out to the tombstone without submitting.
    if (e.target === e.currentTarget) setFormOpen(false);
  };

  const handleStartSwipe = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    startYCard2.current = touch.pageY;
  };

  const handleSwipeDismiss = (event: TouchEvent) => {
    const touch = event.changedTouches[0];
    const endY = touch.pageY;
    if (endY > startYCard2.current) setFormOpen(false);
  };

  useEffect(() => {
    // Russian language renders differently, so we need to override the default font size
    selectedLanguage.code === "Ru"
      ? setShortDescStyle({ fontSize: "14px" })
      : setShortDescStyle({});
  }, [selectedLanguage]);

  useEffect(() => {
    if (!imgLoaded || !showEmailForm) return;
    const doc = document.documentElement;
    doc.style.setProperty("--doc-height", `${window.innerHeight}px`);
    doc.style.setProperty("--email-height", isFirefox ? "0px" : "40px");
    const documentHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty("--doc-height", `${window.innerHeight}px`);
      doc.style.setProperty("--email-height", isFirefox ? "0px" : "40px");
    };

    // Add handlers for card-2 swipe down interaction
    const content = document.getElementById("card-content");
    content.addEventListener("touchstart", handleStartSwipe);
    content.addEventListener("touchend", handleSwipeDismiss);
    window.addEventListener("resize", documentHeight);
    content.addEventListener("wheel", (e) => {
      if (e.deltaY < 0) {
        setFormOpen(false);
      }
    });

    // Disable scrolling inside element
    const card2 = document.getElementById("card-2");
    card2.addEventListener("touchmove", (e: TouchEvent) => e.preventDefault());

    // Trigger to open card-2 once bottom of content is reached
    const trigger = document.getElementById("card-2__trigger");
    trigger.addEventListener("wheel", (e: WheelEvent) => {
      if (e.deltaY > 0) {
        setFormOpen(true);
      }
    });
    trigger.addEventListener("touchstart", (e: TouchEvent) => {
      startYTrigger.current = e.changedTouches[0].pageY;
    });
    trigger.addEventListener("touchend", (e: TouchEvent) => {
      if (e.changedTouches[0].pageY < startYTrigger.current) setFormOpen(true);
    });
    return () => {
      window.removeEventListener("resize", documentHeight);
    };
  }, [artwork, imgLoaded, showEmailForm]);

  // FOC-203: pieces shared by the built-in page and the templated page (where the
  // tenant's server-rendered template places them via system slots).
  const renderedPage = result.data.renderedPage;
  const heroTop = (
    <div className="card-top-container">
      <div className="card-img-overlay">
        <div className="card-header h1">Focused Artwork</div>
        {!imgError && (
          <div className="card-img-result">
            <img
              src={artwork.url}
              alt="match_image"
              role="img"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgError(true)}
              aria-label={`${artwork.title} by ${artwork.artist}${
                artwork.culture ? `, ${artwork.culture}.` : "."
              } ${artwork.visualDescription}`}
              style={{
                opacity: imgLoaded ? 1 : 0,
                transition: "opacity 0.2s ease-in",
              }}
            />
          </div>
        )}
        <div className="card-artist">{artwork.artist}</div>
        <div className="card-title">{artwork.title}</div>
      </div>
    </div>
  );
  const tools = (
    <div className="share-wrapper">
      {/* Language options button */}
      <div className="language-dropdown-wrapper">
        <div className="language-dropdown">
          <LanguageDropdown
            langOptions={langOptions}
            selected={selectedLanguage}
            onSelectLanguage={onSelectLanguage}
          />
        </div>
      </div>

      {/* Share options button for Barnes Collection objects */}
      {/* TODO: determine if we want share button for special exhibition objects */}
      {!result.data.specialExhibition && (
        <Share
          shareText={getTranslation("Result_page", "text_1")}
          artwork={artwork}
        />
      )}
    </div>
  );
  const blocks = artwork.content ? (
    <div className="card-content">
      {artwork.content.map((c, index) => (
        <ContentBlock contentBlock={c.contentBlock} key={index} />
      ))}
    </div>
  ) : null;
  const disclaimer = (
    <>
      {(artwork.content || artwork.shortDescription) &&
        selectedLanguage.code !== LANGUAGE_EN && (
          <div className="google-translate-disclaimer">Translated with AWS</div>
        )}
    </>
  );

  return (
    <div
      id="container"
      className={classnames(
        "exhibition-obj",
        isAndroid ? "sm-container" : "ios-container"
      )}
    >
      <div id="card-content" className="exhibition-obj__content">
        <Fragment>
          <div id="card-1" className="exhibition-obj__content__result">
            <div className="row">
              {!imgError && (
                <div className="artwork-top-bg">
                  {/*
                      Background is the SAME source image as the main display,
                      blurred via CSS filter. Browser fetches once; cache hit
                      for the main render below. Avoids the second Imgix
                      transform that previously took ~700ms on cold cache.
                    */}
                  <img
                    className="card-img-top"
                    src={artwork.url}
                    alt="match_image_background"
                    aria-hidden={true}
                    style={{
                      filter: "blur(25px)",
                      transform: "scale(1.1)",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
              <div className="col-12 col-md-12">
                {renderedPage ? (
                  <div
                    id="result-card"
                    className="card templated-card"
                    data-title={artwork.title}
                    data-artist={artwork.artist}
                    data-id={artwork.id}
                    data-invno={artwork.invno}
                    data-nodesc-invno={
                      !artwork.shortDescription ? artwork.invno : ""
                    }
                  >
                    <TemplatedResult
                      html={renderedPage}
                      hero={heroTop}
                      tools={
                        <div className="card-body result-tools-slot">
                          {tools}
                        </div>
                      }
                      body={blocks}
                    />
                    {/* The template places the translation note ({{translation_note}}). */}
                  </div>
                ) : (
                  <div
                    id="result-card"
                    className="card"
                    data-title={artwork.title}
                    data-artist={artwork.artist}
                    data-id={artwork.id}
                    data-invno={artwork.invno}
                    data-nodesc-invno={
                      !artwork.shortDescription ? artwork.invno : ""
                    }
                  >
                    {heroTop}
                    <div className="card-body" id="focussed-artwork-body">
                      {tools}

                      <div className="short-desc-container">
                        {/* Content blocks from Hygraph CMS */}
                        {artwork.content ? (
                          blocks
                        ) : artwork.shortDescription ? (
                          <div
                            className="card-text paragraph"
                            style={shortDescFontStyle}
                            dangerouslySetInnerHTML={{
                              __html: sanitizeRichText(
                                artwork.shortDescription
                              ),
                            }}
                          ></div>
                        ) : (
                          <></>
                        )}
                      </div>

                      {disclaimer}

                      <div className="card-info">
                        <table className="detail-table">
                          <tbody>
                            <tr>
                              <td className="text-left item-label">{`${getTranslation(
                                "Result_page",
                                "text_3"
                              )}:`}</td>
                              <td className="text-left item-info">
                                {artwork.artist}{" "}
                                {!artwork.unIdentified && artwork.nationality
                                  ? `(${artwork.nationality}, ${artwork.birthDate} - ${artwork.deathDate})`
                                  : ""}
                              </td>
                            </tr>
                            {artwork.unIdentified && (
                              <tr>
                                <td className="text-left item-label">
                                  {`${getTranslation(
                                    "Result_page",
                                    "text_10"
                                  )}:`}
                                </td>
                                <td className="text-left item-info">
                                  {artwork.culture}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td className="text-left item-label">{`${getTranslation(
                                "Result_page",
                                "text_4"
                              )}:`}</td>
                              <td className="text-left item-info">
                                {artwork.title}
                              </td>
                            </tr>
                            {artwork.displayDate && (
                              <tr>
                                <td className="text-left item-label">{`${getTranslation(
                                  "Result_page",
                                  "text_5"
                                )}:`}</td>
                                <td className="text-left item-info">
                                  {artwork.displayDate}
                                </td>
                              </tr>
                            )}
                            {artwork.medium && (
                              <tr>
                                <td className="text-left item-label">{`${getTranslation(
                                  "Result_page",
                                  "text_6"
                                )}:`}</td>
                                <td className="text-left item-info">
                                  {artwork.medium}
                                </td>
                              </tr>
                            )}
                            {artwork.dimensions && (
                              <tr>
                                <td className="text-left item-label">{`${getTranslation(
                                  "Result_page",
                                  "text_7"
                                )}:`}</td>
                                <td className="text-left item-info">
                                  {artwork.dimensions}
                                </td>
                              </tr>
                            )}
                            {artwork.creditLine && (
                              <tr>
                                <td className="text-left item-label">
                                  {/* TODO: Get translations for this label */}
                                  Credit:
                                </td>
                                <td className="text-left item-info">
                                  {formatHtmlCaption(artwork.creditLine.html)}
                                </td>
                              </tr>
                            )}
                            {!artwork.curatorialApproval && (
                              <tr>
                                <td className="text-left item-label">
                                  {`${getTranslation(
                                    "Result_page",
                                    "text_8"
                                  )}:`}
                                </td>
                                <td className="text-left item-info">
                                  {getTranslation("Result_page", "text_9")}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div id="card-2__trigger" />
          </div>
          {showEmailForm ? (
            <div
              id="card-2"
              className={classnames("exhibition-obj__content__email", "card", {
                open: formOpen,
                completed: !formOpen && emailCaptured,
              })}
              onClick={handleClick}
              onTouchStart={(e) => {
                startYPanel.current = e.changedTouches[0].clientY;
              }}
              onTouchEnd={(e) => {
                // Flick DOWN on the panel → close (back to the tombstone),
                // for when you opened it but don't want to enter an email.
                // clientY-based; a large-enough downward delta distinguishes a
                // deliberate dismiss-flick from tapping into the field.
                if (
                  startYPanel.current != null &&
                  e.changedTouches[0].clientY > startYPanel.current + 24
                ) {
                  setFormOpen(false);
                }
              }}
            >
              {/* Tap-to-close grip at the top of the panel. The email form
                    fills the panel, so there's no bare background to tap —
                    this gives a clear close target (flick-down on the panel
                    also closes, via the handlers above). */}
              <button
                type="button"
                className="email-panel-grip"
                onClick={() => setFormOpen(false)}
                aria-label="Close"
              >
                <span className="email-panel-grip__bar" aria-hidden="true" />
              </button>
              <EmailForm
                withStory={false}
                isEmailScreen={false}
                onSubmitEmail={handleSubmitEmail}
                getTranslation={getTranslation}
                getSize={(height) => null}
                pointerEvents="auto"
                handleClickScroll={(storyIndex, isStoryCard) => null}
                alwaysFloatBtn={true}
              />
            </div>
          ) : (
            <ScanButton float={false} />
          )}
          {/* Visible TAP-to-open affordance for the email card. (Swipe-up-to-
                open is handled by the #card-2__trigger on the content — mid-page,
                so it's not eaten by the OS bottom-edge gesture. A flick-up ON
                this bottom-edge tab is NOT wired: the OS home-bar/back gesture
                claims upward swipes from the screen's bottom edge, so it can't be
                caught reliably — tap is the tab's job, swipe is the trigger's.) */}
          {showEmailForm && !formOpen && !emailCaptured && (
            <button
              type="button"
              className="email-pull-tab"
              onClick={() => setFormOpen(true)}
              aria-label={getTranslation("Bookmark_capture", "text_1")}
            >
              <span className="email-pull-tab__grip" aria-hidden="true" />
              <span className="email-pull-tab__label">
                {getTranslation("Bookmark_capture", "text_1")}
              </span>
            </button>
          )}
        </Fragment>
      </div>
    </div>
  );
};
